import { useState, useEffect } from 'react';
import { Layout } from '@/components/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { WysiwygEditor } from '@/components/WysiwygEditor';
import { toast } from 'sonner';
import { FileText, Save, Loader2, ExternalLink, Eye } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const AdminAGBPage = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [lastUpdated, setLastUpdated] = useState(null);
  const [updatedBy, setUpdatedBy] = useState(null);

  useEffect(() => {
    fetchAGB();
  }, []);

  const fetchAGB = async () => {
    try {
      const token = localStorage.getItem('shrt_token');
      const response = await fetch(`${API}/api/admin/agb`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setTitle(data.title || 'Allgemeine Geschäftsbedingungen');
        setContent(data.content || '');
        setLastUpdated(data.updated_at);
        setUpdatedBy(data.updated_by);
      }
    } catch (error) {
      console.error('Error fetching AGB:', error);
      toast.error('Fehler beim Laden der AGB');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('shrt_token');
      const response = await fetch(`${API}/api/admin/agb`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ title, content })
      });
      
      if (response.ok) {
        toast.success('AGB erfolgreich gespeichert');
        fetchAGB(); // Reload to get updated timestamp
      } else {
        const errorData = await response.json();
        toast.error(errorData.detail || 'Fehler beim Speichern');
      }
    } catch (error) {
      console.error('Error saving AGB:', error);
      toast.error('Fehler beim Speichern der AGB');
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Nie';
    return new Date(dateString).toLocaleString('de-CH', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[400px]">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl font-['Oswald'] font-bold tracking-wider uppercase flex items-center gap-3">
            <FileText className="text-primary" />
            AGB Verwalten
          </h1>
          <p className="text-muted-foreground mt-2">
            Bearbeiten Sie die Allgemeinen Geschäftsbedingungen, die bei der Zahlung akzeptiert werden müssen.
          </p>
        </div>

        {/* Info Card */}
        <Card className="mb-6 border-primary/30 bg-primary/5">
          <CardContent className="pt-4">
            <div className="flex items-start gap-3">
              <Eye className="w-5 h-5 text-primary mt-0.5" />
              <div className="text-sm">
                <p className="font-medium mb-1">Öffentliche AGB-Seite</p>
                <p className="text-muted-foreground mb-2">
                  Die AGB sind öffentlich unter folgender URL erreichbar:
                </p>
                <a 
                  href={`${window.location.origin}/agb`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-primary hover:underline"
                  data-testid="agb-public-link"
                >
                  {window.location.origin}/agb
                  <ExternalLink className="w-3 h-3" />
                </a>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Edit Form */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg uppercase tracking-wide">AGB bearbeiten</CardTitle>
            <CardDescription>
              {lastUpdated ? (
                <>Zuletzt aktualisiert: {formatDate(lastUpdated)}{updatedBy && ` von ${updatedBy}`}</>
              ) : (
                'Noch keine AGB hinterlegt'
              )}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Title */}
            <div className="space-y-2">
              <label className="text-sm font-medium uppercase tracking-wide">Titel</label>
              <Input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Allgemeine Geschäftsbedingungen"
                className="bg-background"
                data-testid="agb-title-input"
              />
            </div>

            {/* Content */}
            <div className="space-y-2">
              <label className="text-sm font-medium uppercase tracking-wide">Inhalt</label>
              <WysiwygEditor
                value={content}
                onChange={(html) => setContent(html)}
                placeholder="Geben Sie hier Ihre AGB ein..."
              />
              <p className="text-xs text-muted-foreground">
                Verwenden Sie die Toolbar für Formatierungen wie Überschriften, Listen und Links.
              </p>
            </div>

            {/* Save Button */}
            <div className="flex justify-end pt-4 border-t">
              <Button
                onClick={handleSave}
                disabled={saving}
                className="uppercase tracking-wider"
                data-testid="save-agb-btn"
              >
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Speichern...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    AGB speichern
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default AdminAGBPage;
